import { ArrowLeftOutlined, ThunderboltFilled } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { type Dispatch } from "@reduxjs/toolkit";
import {
  Alert,
  Button,
  Card,
  ConfigProvider,
  Divider,
  Flex,
  Form,
  Input,
  Layout,
  Typography,
} from "antd";
import { Content } from "antd/es/layout/layout";
import React, { useState } from "react";
import { connect } from "react-redux";
import { actions as routerActions } from "redux-router5";
import { silentClient } from "../api-client";
import { routeNames } from "../common/routes";
import type { RootState } from "../store";
import { loginTheme } from "../theme";
import styles from "./ForgotPasswordPage.module.css";

type ForgotPasswordFieldType = {
  email: string;
};

type Props = {
  dispatch: Dispatch;
  intl: IntlShape;
};

const ForgotPasswordPage: React.FC<Props> = ({ dispatch, intl }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasRequestedReset, setHasRequestedReset] = useState(false);
  const [errorMessageKey, setErrorMessageKey] = useState<string | null>(null);

  const navigateToLogin = () => {
    dispatch(routerActions.navigateTo(routeNames.USER_LOGIN));
  };

  const handleSubmit = async (values: ForgotPasswordFieldType) => {
    setIsSubmitting(true);
    setErrorMessageKey(null);
    try {
      await silentClient.apiV1ManagementAuthResetPasswordCreate({
        email: values.email,
      });
      setHasRequestedReset(true);
    } catch (error: unknown) {
      const failure = error as { code?: string };
      if (failure?.code === "ERR_NETWORK") {
        setErrorMessageKey("forgot_password_error_network");
      } else {
        setHasRequestedReset(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCardContent = () => {
    if (hasRequestedReset) {
      return (
        <>
          <Alert
            type="success"
            title={intl.formatMessage({ id: "forgot_password_success_title" })}
            description={intl.formatMessage({ id: "forgot_password_success_message" })}
            className={styles.successAlert}
            showIcon
          />
          <Flex justify="center" className={styles.backLinkWrapper}>
            <Typography.Link onClick={navigateToLogin}>
              <ArrowLeftOutlined />{" "}
              {intl.formatMessage({ id: "forgot_password_back_to_login" })}
            </Typography.Link>
          </Flex>
        </>
      );
    }

    return (
      <>
        <Typography.Title level={4} className={styles.heading}>
          {intl.formatMessage({ id: "forgot_password_header" })}
        </Typography.Title>
        <Typography.Text className={styles.subHeading}>
          {intl.formatMessage({ id: "forgot_password_subheader" })}
        </Typography.Text>

        {errorMessageKey && (
          <Alert
            type="error"
            message={intl.formatMessage({ id: errorMessageKey })}
            className={styles.errorAlert}
            showIcon
          />
        )}

        <Form
          name="forgotPassword"
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
          className={styles.form}
        >
          <Form.Item<ForgotPasswordFieldType>
            label={
              <Typography.Text className={styles.label}>
                {intl.formatMessage({ id: "forgot_password_field_email" })}
              </Typography.Text>
            }
            name="email"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "forgot_password_input_email_missing",
                }),
              },
              {
                type: "email",
                message: intl.formatMessage({
                  id: "forgot_password_input_email_invalid",
                }),
              },
            ]}
          >
            <Input
              size="large"
              placeholder={intl.formatMessage({ id: "forgot_password_field_email" })}
            />
          </Form.Item>

          <Form.Item label={null} className={styles.submitFormItem}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={isSubmitting}
              disabled={isSubmitting}
              style={{ fontWeight: 600 }}
            >
              {intl.formatMessage({ id: "forgot_password_button_submit" })}
            </Button>
          </Form.Item>

          <Flex justify="center" className={styles.backLinkWrapper}>
            <Typography.Link onClick={navigateToLogin}>
              <ArrowLeftOutlined />{" "}
              {intl.formatMessage({ id: "forgot_password_back_to_login" })}
            </Typography.Link>
          </Flex>
        </Form>
      </>
    );
  };

  return (
    <ConfigProvider theme={loginTheme}>
      <Layout>
        <Content>
          <Flex vertical className={styles.pageWrapper} align="center" justify="center">
            <Card className={styles.card}>
              <Flex align="center" gap={12} className={styles.brand}>
                <Flex align="center" justify="center" className={styles.iconWrap}>
                  <ThunderboltFilled className={styles.brandIcon} />
                </Flex>
                <Flex vertical>
                  <Typography.Text className={styles.brandName}>
                    DZ Projects
                  </Typography.Text>
                  <Typography.Text className={styles.brandTag}>
                    Wind Asset Management
                  </Typography.Text>
                </Flex>
              </Flex>

              <Divider className={styles.divider} />

              {renderCardContent()}
            </Card>
          </Flex>
        </Content>
      </Layout>
    </ConfigProvider>
  );
};

export const ConnectedForgotPasswordPage = connect((state: RootState) => ({
  intl: state.intl.datetimeIntl,
}))(ForgotPasswordPage);

export default ConnectedForgotPasswordPage;
